/** The Street (screens §P4.7): the latest published narrative for the viewer's bank, labelled AI-drafted and facts-checked. */
import type { MarketView, RenderedNarrative } from "@bsim/contracts";
import { Icon } from "../theme/icons";
import { GxPill } from "../theme/GxPill";
import { GxCard } from "../theme/primitives";

const KIND_LABELS: Readonly<Record<RenderedNarrative["kind"], string>> = {
  analyst_note: "Analyst note", press_item: "Press item", market_wrap: "Market wrap",
  supervisory_letter: "Supervisory letter", customer_sentiment: "Customer sentiment",
};

/** Latest published item about this bank; an analyst note wins over other kinds. */
export function streetNarrative(news: MarketView["news"], teamId: string): RenderedNarrative | null {
  const own = news.filter((n) => n.teamId === teamId);
  return own.filter((n) => n.kind === "analyst_note").at(-1) ?? own.at(-1) ?? null;
}

/** The engine's note calls the bank "Your bank"; the page names it. */
const named = (text: string, bankName: string): string => text.replace(/\bYour bank\b/g, bankName);

export function TheStreet({ narrative, fyLabel, released, bankName }: { readonly narrative: RenderedNarrative | null; readonly fyLabel: string; readonly released: boolean; readonly bankName: string }) {
  // Before the first release there is nothing to show: one line, not an empty card.
  if (narrative === null && !released) return <p className="gx-small gx-muted" data-testid="cockpit.street">The Street&apos;s note on your bank arrives with your first results.</p>;
  if (narrative === null) {
    return <GxCard title="The Street" testId="cockpit.street"><div className="gx-card-b gx-muted">The Street&apos;s note on your bank is not available yet.</div></GxCard>;
  }
  const template = narrative.status === "fallback_template";
  return (
    <section className="gx-card" data-testid="cockpit.street">
      <div className="gx-card-h gx-cockpit-street-head">
        <div>
          <div className="gx-eyebrow gx-cockpit-street-eyebrow">
            <Icon name="quote" size={14} />The Street on {fyLabel} · {KIND_LABELS[narrative.kind]} · {template ? "Template" : "AI-drafted"}
          </div>
          <h2 className="gx-h2 gx-cockpit-street-h">{named(narrative.headline, bankName)}</h2>
        </div>
        {template ? null : <GxPill tone="info"><Icon name="check" size={12} /> Facts checked</GxPill>}
      </div>
      <div className="gx-card-b gx-narr">
        {narrative.bodyMarkdown.split(/\n{2,}/).map((p) => <p key={p}>{named(p, bankName)}</p>)}
        <div className="gx-small gx-faint gx-cockpit-narr-by">Drafted from engine facts · published with the {fyLabel} results</div>
      </div>
    </section>
  );
}
